import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { LoginCallback, useOktaAuth } from '@okta/okta-react';
import OktaError from './OktaError';
import {
  getSilentAuthError,
  takeSsoReturnPath,
  clearOktaRedirectState,
  markSsoSessionChecked,
} from './ssoRedirect';
import {
  getAccessDeniedError,
  getLoginPath,
  recordAccessDenied,
  clearAccessDenied,
} from './loginAccessDenied';
import reportAccessDenied from './reportAccessDenied';
import { getUserTypeLoginUrl } from '../utilities/UtilityFunctions';
import { promiseResolver } from '../utilities/CommonApiUtils';

/**
 * Okta callback route.
 *
 * On top of the regular token exchange it handles the answer of the `prompt=none`
 * SSO check started from the sign in page: when there is no Okta session the callback
 * carries `error=login_required` and no code, so instead of reporting an error we go
 * back to the login page and let the user sign in with the widget.
 *
 * It also handles `error=access_denied` (user not assigned to the app): we go back to
 * the login page, which explains the problem, instead of leaving the user on a blank
 * callback page.
 */
const OktaLoginCallback = ({ history }) => {
  const { oktaAuth } = useOktaAuth();
  // The callback URL, read once: after `history.replace` the component can render
  // again with the login page's URL, which must not read as a new callback.
  const [search] = useState(() => window.location.search);
  const silentAuthError = getSilentAuthError(search);
  const isAccessDenied = Boolean(getAccessDeniedError(search));

  useEffect(() => {
    if (!silentAuthError) {
      return;
    }
    const returnPath = takeSsoReturnPath() || getUserTypeLoginUrl();
    clearOktaRedirectState();
    history.replace(returnPath);
  }, [silentAuthError, history]);

  useEffect(() => {
    if (!isAccessDenied) {
      return;
    }
    const loginPath =
      takeSsoReturnPath() || getLoginPath() || getUserTypeLoginUrl();
    reportAccessDenied(getAccessDeniedError(search), {
      ...recordAccessDenied(),
      loginPath,
    });

    // Another `prompt=none` round trip would only bring the same answer back.
    markSsoSessionChecked();
    clearOktaRedirectState();
    // Drop the Okta session, otherwise the login page picks it up and fails again.
    promiseResolver(oktaAuth.closeSession()).then(() =>
      history.replace(loginPath)
    );
  }, [isAccessDenied, search, history, oktaAuth]);

  // A regular callback means Okta let this sign in through, so a denial kept
  // from an earlier attempt no longer applies. The login page only clears it
  // when it renders signed in, which a successful sign in skips, so without
  // this the old error came back after sign out.
  useEffect(() => {
    if (silentAuthError || isAccessDenied) {
      return;
    }
    clearAccessDenied();
  }, [silentAuthError, isAccessDenied]);

  if (silentAuthError || isAccessDenied) {
    return null;
  }

  return <LoginCallback errorComponent={OktaError} />;
};

export default withRouter(OktaLoginCallback);
